import { http, getVisitorId, trackEvent } from "@/lib/api";
import { toast } from "sonner";

// Spec event names for CTA clicks.
const CTA_EVENT = {
  diagnostic: "clarity_cta_clicked", blueprint: "blueprint_cta_clicked",
  guided: "guided_consulting_cta_clicked", guided_standalone: "guided_consulting_cta_clicked",
  inhome_overnight: "in_home_cta_clicked", is101_tuition: "is101_enroll_clicked", ns101_tuition: "ns101_enroll_clicked", is201_tuition: "is201_enroll_clicked", is301_tuition: "is301_enroll_clicked", is401_tuition: "is401_enroll_clicked",
  is101_faculty: "is101_faculty_enroll_clicked", ns101_faculty: "ns101_faculty_enroll_clicked", is201_faculty: "is201_faculty_enroll_clicked", is301_faculty: "is301_faculty_enroll_clicked", is401_faculty: "is401_faculty_enroll_clicked",
};

// GA4 ecommerce labels + list prices (USD) for begin_checkout items.
const GA4_ITEM = {
  diagnostic: { name: "My Clarity", price: 99 },
  blueprint: { name: "Personalized Sleep Blueprint", price: 279 },
  guided: { name: "Guided Virtual Consulting", price: 749 },
  guided_standalone: { name: "Guided Virtual Consulting (Standalone)", price: 749 },
  inhome_overnight: { name: "In-Home Overnight", price: 2549 },
  is101_tuition: { name: "IS-101 Tuition", price: 99 },
  ns101_tuition: { name: "NS-101 Tuition", price: 149 },
  is201_tuition: { name: "IS-201 Tuition", price: 299 },
  is301_tuition: { name: "IS-301 Tuition", price: 299 },
  is401_tuition: { name: "IS-401 Tuition", price: 299 },
  is101_faculty: { name: "IS-101 Faculty-Led Tuition", price: 299 },
  ns101_faculty: { name: "NS-101 Faculty-Led Tuition", price: 349 },
  is201_faculty: { name: "IS-201 Faculty-Led Tuition", price: 399 },
  is301_faculty: { name: "IS-301 Faculty-Led Tuition", price: 399 },
  is401_faculty: { name: "IS-401 Faculty-Led Tuition", price: 399 },
};

// Course + learning format GA4 item (no personal data).
export function courseItem(product) {
  const ga = GA4_ITEM[product] || { name: product, price: 0 };
  const m = /^(is101|ns101|is201|is301|is401)_(tuition|faculty)$/.exec(product || "");
  const fmt = m ? (m[2] === "faculty" ? "faculty_led" : "independent_study") : undefined;
  const code = m ? `${m[1].slice(0, 2)}-${m[1].slice(2)}`.toUpperCase() : undefined;
  return { value: ga.price, product_id: product, offer: ga.name, ...(m ? { course_code: code, learning_format: fmt } : {}),
    items: [{ item_id: product, item_name: ga.name, price: ga.price, quantity: 1, ...(m ? { item_category: "Course Tuition", item_category2: code, item_variant: fmt, item_brand: "Infant Sleep Consultant Near Me Education Center" } : {}) }] };
}

// Starts Stripe checkout. Requires an authenticated customer; otherwise routes to register.
export async function startCheckout({ product, user, navigate, cohortId, slotStart }) {
  const lookupKeys = {
    diagnostic: "isc_diagnostic", blueprint: "isc_blueprint",
    guided: "isc_guided", guided_standalone: "isc_guided_standalone",
    inhome_overnight: "isc_inhome_overnight", is101_tuition: "isc_is101_tuition", ns101_tuition: "isc_ns101_tuition", is201_tuition: "isc_is201_tuition", is301_tuition: "isc_is301_tuition", is401_tuition: "isc_is401_tuition",
    is101_faculty: "isc_is101_faculty", ns101_faculty: "isc_ns101_faculty", is201_faculty: "isc_is201_faculty", is301_faculty: "isc_is301_faculty", is401_faculty: "isc_is401_faculty",
  };
  trackEvent(CTA_EVENT[product] || `${product}_cta_clicked`, { product_id: product, offer: (GA4_ITEM[product] || {}).name || product });
  // Courses fire add_to_cart on the Enrollment Review page; consulting offers have no review step, so fire it on the buy click.
  if (!/_(tuition|faculty)$/.test(product)) trackEvent("add_to_cart", { currency: "USD", ...courseItem(product) }, { once: true, key: `cart-${product}` });
  if (!user) {
    localStorage.setItem("isc_pending_product", product);
    navigate(/_(tuition|faculty)$/.test(product) ? `/register?next=${encodeURIComponent(`/enroll/${product}`)}` : `/register?product=${product}`);
    return;
  }
  if (user.role !== "customer") {
    toast.error("Purchases are made from a customer account.");
    return;
  }
  try {
    const { data } = await http.post("/payments/checkout", {
      lookup_key: lookupKeys[product],
      origin_url: window.location.origin,
      user_id: user.id,
      visitor_id: getVisitorId(),
      cohort_id: cohortId,
      slot_start: slotStart,
    });
    try { localStorage.setItem("isc_pending_product", product); } catch (e) {}
    trackEvent("begin_checkout", {
      currency: "USD",
      landing_page: window.location.pathname,
      ...courseItem(product),
    });
    window.location.href = data.checkout_url;
  } catch (e) {
    const detail = e?.response?.data?.detail;
    toast.error(typeof detail === "string" ? detail : "Could not start checkout. Please try again.");
  }
}
